import {
  Button,
  DocumentFileInput,
  DocumentValues,
  DownloaderLoading,
  feedbackcard,
  InformationCard,
  Success,
  useDisplayUserError,
  useIsMobile,
  useUpload,
  type UploadFileResult
} from '@yes.technology/react-toolkit'
import useDocument from 'infrastructure/shared/hooks/useDocument/useDocument'
import useFileUpload from 'infrastructure/shared/hooks/useFileUpload/useFileUpload'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { SITUATION_CODE_ACTIVE } from 'site/Renderer/components/DataTables/hooks/useTableActionSave/useTableActionSave'
import useTableContext from 'site/Renderer/components/DataTables/hooks/useTableContext'
import useObjectFields from 'site/Renderer/components/ObjectView/hooks/useObjectFields'
import useInitialStatus from 'status/shared/hooks/useInitialStatus'
import { v4 as uuidv4 } from 'uuid'
import patchObject from 'yesObject/shared/api/patchObject'
import postObject from 'yesObject/shared/api/postObject'
import useYesObject from 'yesObject/shared/hooks/useYesObject'
import { ButtonContainer, Container } from './InformationDocumentUpload.styles'

enum Step {
  UPLOAD = 'upload',
  SUCCESS = 'success'
}

const LOGO_URL = 'https://d2qqjx0lx9cu56.cloudfront.net/logos/logo-yes.svg'

type InformationalDocumentUploadProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const InformationalDocumentUpload = ({
  isOpen,
  onClose,
  onSuccess
}: InformationalDocumentUploadProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [step, setStep] = useState<Step>(Step.UPLOAD)

  const [inputValue, setInputValue] = useState<DocumentValues>({})
  const {
    getPostSignedUploadUrl,
    onFileUpload,
    getStatusMessage,
    isLoading,
    setIsLoading: handleFileUploadLoadingState
  } = useFileUpload()

  const {
    idObjectClass = '',
    fields,
    uploadAcceptedFileTypes
  } = useTableContext()

  const [searchParams] = useSearchParams()
  const mainObjectUuid = searchParams.get('object-uuid') || ''
  const mainObjectObjectClass = searchParams.get('uuid_objectclass') || ''

  const { displayUserError } = useDisplayUserError()

  const { createOrUpdateDocument } = useDocument()

  const { fetchObjects } = useYesObject({
    objectUUID: mainObjectUuid,
    objectclassUUID: mainObjectObjectClass,
    skipMetadata: true,
    skipRelations: false,
    enabled: false
  })

  const { relationFields } = useObjectFields({
    objectclassUuid: mainObjectObjectClass,
    enabled: !!mainObjectObjectClass
  })

  const initialStatus = useInitialStatus({
    objectclassUuid: idObjectClass
  })

  const relationColumnName = useMemo(() => {
    if (!mainObjectObjectClass || !idObjectClass) return ''
    return (
      relationFields.find(
        (relation) => relation?.relation?.object_class?.uuid === idObjectClass
      )?.column_json || ''
    )
  }, [idObjectClass, relationFields, mainObjectObjectClass])

  const { uploadFile } = useUpload({
    getPostSignedUploadUrl,
    onFileUpload,
    getStatusMessage
  })

  const handleFileUpload = useCallback(
    async (file: File): Promise<UploadFileResult> => {
      return uploadFile({ file, fileType: file.type })
    },
    [uploadFile]
  )

  const handleUploadSuccess = useCallback(
    async (result: UploadFileResult) => {
      const documentValues: DocumentValues = {
        contentType: result.content.type,
        size: result.content.size,
        url: result.url,
        filename: result.content.name,
        originalFilename: result.content.name,
        uuid_status: t('upload-status.awaiting-activation')
      }

      setInputValue(documentValues)
    },
    [t]
  )

  const handleUploadError = useCallback(
    (error: Error) => {
      console.error(error)

      handleFileUploadLoadingState(false)
      setInputValue({})

      displayUserError({
        error: new Error(t('upload-status.upload-error')),
        title: t('upload-status.upload-error'),
        userMessageFallback: t('upload-status.upload-error')
      })
    },
    [handleFileUploadLoadingState, t, displayUserError]
  )

  const handleDescriptionChange = useCallback(
    (value: string, currentValues: DocumentValues) => {
      const updatedValues = {
        ...currentValues,
        des: value
      }
      setInputValue(updatedValues)
    },
    []
  )

  const handleDescriptionSave = useCallback(async () => {
    // TODO: Implement description save when necessary or turn it optional in next version of toolkit
  }, [])

  const handleClear = useCallback(() => {
    setInputValue({})
  }, [])

  const linkDocumentToMainObject = useCallback(
    async (documentUuid: string) => {
      if (!mainObjectUuid || !relationColumnName) {
        return
      }

      const mainObjectResult = await fetchObjects()

      if (!mainObjectResult?.data?.objects?.length) {
        return
      }

      const lastVersion = mainObjectResult?.data?.objects?.[0]?.version || 0
      const currentRelations = (mainObjectResult?.data?.objects?.[0]?.[
        relationColumnName
      ] || []) as Array<{ uuid: string }>

      const documentAlreadyLinked = currentRelations.some(
        (relation) => relation.uuid === documentUuid
      )

      if (documentAlreadyLinked) {
        return
      }

      await patchObject({
        uuid: mainObjectUuid,
        data: {
          version: lastVersion,
          [relationColumnName]: [
            ...currentRelations.map((relation) => ({ uuid: relation.uuid })),
            { uuid: documentUuid }
          ]
        },
        showLoading: true
      })
    },
    [mainObjectUuid, relationColumnName, fetchObjects]
  )

  const handleSaveAndActivate = useCallback(async () => {
    if (!inputValue?.url) {
      return
    }

    const requestUuid = uuidv4()

    dispatch({
      type: 'api/REQUESTED',
      data: {
        requestUuid
      }
    })

    try {
      const fileInfo = inputValue

      if (!fileInfo.url || !fileInfo.contentType) {
        displayUserError({
          error: new Error(t('data-tables.error')),
          title: t('data-tables.error'),
          userMessageFallback: t('data-tables.error')
        })
        return
      }

      const { document } = await createOrUpdateDocument({
        uuid: fileInfo.uuid || '',
        url: fileInfo.url,
        content_type: fileInfo.contentType,
        complement: fileInfo.size
          ? JSON.stringify({ size: fileInfo.size })
          : '',
        des: fileInfo.des
      })

      if (!document?.uuid) {
        throw new Error('Failed to create document')
      }

      await linkDocumentToMainObject(document.uuid)

      const documentField = Object.keys(fields || {}).find((fieldName) => {
        const field = fields?.[fieldName]
        return (
          field?.type === 'RELATION' &&
          field?.relation?.object_class?.uuid === window.idObjectclassDocument
        )
      })

      if (documentField && idObjectClass && initialStatus?.uuid) {
        await postObject({
          uuid_Objectclass: idObjectClass,
          uuid_status: initialStatus.uuid,
          situation: SITUATION_CODE_ACTIVE,
          data: {
            [documentField]: [{ uuid: document.uuid }]
          },
          showLoading: true
        })
      }

      feedbackcard(t('data-tables.success'), {
        type: 'success',
        message: t('data-tables.success')
      })

      setStep(Step.SUCCESS)

      setInputValue({})
    } catch (error) {
      displayUserError({
        error: error instanceof Error ? error : new Error(String(error)),
        title: t('data-tables.error'),
        userMessageFallback: t('data-tables.error')
      })
    } finally {
      dispatch({
        type: 'api/RECEIVED',
        data: {
          requestUuid
        }
      })
    }
  }, [
    inputValue,
    dispatch,
    createOrUpdateDocument,
    linkDocumentToMainObject,
    fields,
    idObjectClass,
    initialStatus?.uuid,
    t,
    displayUserError
  ])

  const isMobile = useIsMobile()

  const handleClose = useCallback(() => {
    onClose()
    setInputValue({})
    setStep(Step.UPLOAD)

    if (step === Step.SUCCESS) {
      onSuccess()
    }
  }, [onClose, onSuccess, step])

  return (
    <>
      {isLoading && (
        <DownloaderLoading
          title={t('document-upload.uploading-document-title')}
          description={t('document-upload.uploading-document-description')}
        />
      )}
      <InformationCard
        title=''
        isOpen={isOpen}
        close={handleClose}
        hideHeader
        style={{ width: '100%', maxWidth: isMobile ? '100%' : '829px' }}
      >
        {step === Step.UPLOAD && (
          <>
            <Container>
              <DocumentFileInput
                label={t('document-upload.document-title')}
                inputValue={inputValue}
                onChange={(value) => setInputValue(value as DocumentValues)}
                onFileUpload={handleFileUpload}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
                onDescriptionChange={handleDescriptionChange}
                onDescriptionSave={handleDescriptionSave}
                onClear={handleClear}
                accept={uploadAcceptedFileTypes}
              />
            </Container>
            <ButtonContainer>
              <Button onClick={handleSaveAndActivate}>
                {t('data-tables.save-and-activate')}
              </Button>
              <Button onClick={handleClose} variant='secondary'>
                {t('data-tables.cancel')}
              </Button>
            </ButtonContainer>
          </>
        )}
        {step === Step.SUCCESS && (
          <Container style={{ padding: '40px' }}>
            <Success
              title={t('document-upload.success.title')}
              description={t('document-upload.success.description')}
              logoUrl={LOGO_URL}
            />
            <ButtonContainer>
              <Button onClick={handleClose}>
                {t('document-upload.go-back')}
              </Button>
            </ButtonContainer>
          </Container>
        )}
      </InformationCard>
    </>
  )
}

export default InformationalDocumentUpload
